/**
 * Which jobs must carry an estimated labor-hours figure.
 *
 * ⚠️ MIRRORS `apps/api/src/common/labor-hours.ts`. The API already returns
 * `estimatedHours.state`, and that is what UI should prefer — this exists for
 * the surfaces that hold a project row and want the answer without a second
 * request (the Overview flag, and any future portfolio sweep). If the two
 * disagree, the API is right.
 *
 * The figure is typed by the estimator OR (since 2026-08-18) derived from the
 * job's labor budget ÷ the company rate — `effective` carries whichever applies,
 * so THIS RULE IS UNCHANGED: pass `effective` and it answers. The old Intacct
 * autocalc stays demoted (product owner, 2026-08-04 — it routinely ran low); the derived
 * figure is the estimators' own pricing math run backwards and pre-loads the
 * field per the product owner, 2026-08-18. A job with neither still flags 'missing'.
 */

export type LaborHoursState = 'set' | 'missing' | 'exempt';

/** Statuses where nobody will enter hours, and shouldn't be asked to. */
const CLOSED_STATUSES = new Set(['complete', 'canceled', 'cancelled']);

/**
 * ⚠️ Edit-LOCKED, not merely SYSTAC-prefixed. A won bid keeps its SYSTAC- number
 * until Bid Tracker renames it, and bid_closed_won_at makes it editable in that
 * window — so those jobs must still be flagged. Mirrors isEditLocked in
 * apps/api/src/common/job-number.ts (review, #293).
 */
const isEditLocked = (
  jobNumber: string | null | undefined,
  closedWon?: boolean | string | Date | null,
) => String(jobNumber ?? '').trim().toUpperCase().startsWith('SYSTAC-') && !closedWon;

export function laborHoursState(
  effectiveHours: number | null | undefined,
  statusLabel: string | null | undefined,
  jobNumber: string | null | undefined,
  closedWon?: boolean | string | Date | null,
): LaborHoursState {
  if (effectiveHours != null) return 'set';
  if (CLOSED_STATUSES.has(String(statusLabel ?? '').trim().toLowerCase())) return 'exempt';
  // A LOCKED bid placeholder is not a job yet; a won one is, and gets flagged.
  if (isEditLocked(jobNumber, closedWon)) return 'exempt';
  return 'missing';
}

export const needsLaborHours = (
  effectiveHours: number | null | undefined,
  statusLabel: string | null | undefined,
  jobNumber: string | null | undefined,
  closedWon?: boolean | string | Date | null,
): boolean => laborHoursState(effectiveHours, statusLabel, jobNumber, closedWon) === 'missing';
